import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { pdfExportsApi, PdfExportNoticeItem, PdfExportRecord } from '../api/pdfExportsApi';
import FeeNotificationDeliveryModal from '../components/modals/FeeNotificationDeliveryModal';
import { getApiErrorMessage } from '../utils/apiErrors';

const NOTICES_PAGE_SIZE = 50;

export const FeeNoticeBatchList: React.FC = () => {
  const { exportId = '' } = useParams();
  const [exportInfo, setExportInfo] = useState<PdfExportRecord | null>(null);
  const [items, setItems] = useState<PdfExportNoticeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [onlyWithoutApp, setOnlyWithoutApp] = useState(false);
  const [search, setSearch] = useState('');
  const [sendingNoticeId, setSendingNoticeId] = useState<string | null>(null);
  const [sendTarget, setSendTarget] = useState<PdfExportNoticeItem | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sentNoticeKeys, setSentNoticeKeys] = useState<Set<string>>(() => new Set());

  const noticeSendKey = (apartmentId: string) => `${exportId}:${apartmentId}`;

  const loadData = async () => {
    if (!exportId) return;
    try {
      setLoading(true);
      const data = await pdfExportsApi.getNotices(exportId, {
        page,
        limit: NOTICES_PAGE_SIZE,
        onlyWithoutApp,
        search: search.trim() || undefined,
      });
      setExportInfo(data.export);
      setItems(data.items);
      setTotalPages(data.pagination.totalPages);
      setTotal(data.pagination.total);
      if (data.export.onlyWithoutApp) {
        setOnlyWithoutApp(true);
      }
    } catch (error) {
      console.error(error);
      toast.error('Không thể tải danh sách phiếu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [exportId, page, onlyWithoutApp]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const openSendModal = (item: PdfExportNoticeItem) => {
    setSendTarget(item);
    setShowSendModal(true);
  };

  const handleConfirmSend = async (deliveryMethods: string[]) => {
    if (!sendTarget || !exportId) return;
    try {
      setSendingNoticeId(sendTarget.id);
      const result = await pdfExportsApi.sendNotice(exportId, sendTarget.id, {
        deliveryMethods: deliveryMethods as ('EMAIL' | 'APP')[],
      });
      if (result.success) {
        const parts: string[] = [];
        if (result.emailsSent > 0) parts.push(`${result.emailsSent} email`);
        if (result.appNotificationsSent > 0) parts.push(`${result.appNotificationsSent} push app`);
        toast.success(
          `Đã gửi thông báo phí ${sendTarget.code}${parts.length ? ` (${parts.join(', ')})` : ''}`,
        );
        setSentNoticeKeys((prev) => new Set(prev).add(noticeSendKey(sendTarget.id)));
        setItems((prev) =>
          prev.map((row) =>
            row.id === sendTarget.id
              ? { ...row, feeNoticeSentAt: new Date().toISOString() }
              : row,
          ),
        );
        setShowSendModal(false);
        setSendTarget(null);
      } else {
        toast.error(result.errors.join('; ') || 'Gửi thông báo phí thất bại');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Gửi thông báo phí thất bại'));
    } finally {
      setSendingNoticeId(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <Link
            to="/export-pdf-service-fees"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            &larr; Quay lại xuất phí
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">
            Danh sách phiếu thông báo
          </h1>
          {exportInfo && (
            <p className="text-gray-600 mt-1">
              {exportInfo.title} · {exportInfo.buildingName} · Danh mục:{' '}
              {exportInfo.feeCategoryLabel?.trim() || 'Tất cả'} · Tổng {total} phiếu
            </p>
          )}
        </div>
        {exportId && (
          <button
            type="button"
            onClick={() =>
              window.open(
                pdfExportsApi.getBatchPrintHtmlUrl(exportId, {
                  onlyWithoutApp,
                }),
                '_blank',
              )
            }
            className="inline-flex items-center px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
          >
            IN tất cả
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm mã căn hộ, mã phiếu..."
            className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            Tìm
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={onlyWithoutApp}
              onChange={(e) => {
                setOnlyWithoutApp(e.target.checked);
                setPage(1);
              }}
            />
            Căn hộ chưa cài app
          </label>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">Đang tải...</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-gray-500">Không có phiếu nào</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Mã căn hộ
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Mã phiếu
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Cư dân
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  App
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Ngày tạo
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {item.code}
                  </td>
                  <td className="px-4 py-3 text-sm text-green-700 font-medium">
                    {item.noticeNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.ownerName}</td>
                  <td className="px-4 py-3 text-sm">
                    {item.hasApp ? (
                      <span className="text-green-600">Đã cài</span>
                    ) : (
                      <span className="text-amber-600">Chưa cài</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDateTime(item.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          window.open(
                            pdfExportsApi.getNoticeHtmlUrl(exportId, item.id),
                            '_blank',
                          )
                        }
                        className="p-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                        title="Xem"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          window.open(
                            pdfExportsApi.getNoticeHtmlUrl(exportId, item.id, true),
                            '_blank',
                          )
                        }
                        className="p-2 rounded-full bg-green-600 text-white hover:bg-green-700"
                        title="In"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => openSendModal(item)}
                        disabled={sendingNoticeId === item.id}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                        title="Gửi email hoặc push app (HTML đã lưu trong batch)"
                      >
                        {sendingNoticeId === item.id
                          ? '...'
                          : item.feeNoticeSentAt ||
                              sentNoticeKeys.has(noticeSendKey(item.id))
                            ? 'Gửi lại'
                            : 'Gửi'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <div className="flex flex-wrap justify-between items-center gap-3 mt-4 px-1">
          <p className="text-sm text-gray-600">
            Hiển thị {(page - 1) * NOTICES_PAGE_SIZE + 1}–
            {Math.min(page * NOTICES_PAGE_SIZE, total)} / {total} phiếu
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Trước
              </button>
              <span className="text-sm text-gray-600">
                Trang {page}/{totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Sau
              </button>
            </div>
          )}
        </div>
      )}
      <FeeNotificationDeliveryModal
        isOpen={showSendModal}
        mode="MONTHLY_FEE"
        onClose={() => {
          if (sendingNoticeId) return;
          setShowSendModal(false);
          setSendTarget(null);
        }}
        onConfirm={handleConfirmSend}
        sending={Boolean(sendingNoticeId)}
      />
    </div>
  );
};

export default FeeNoticeBatchList;
